import useSpaceStore from '../stores/spaceStore';
import { windowRouter } from '../stores/windowRouter';

export interface OpenSettingsOptions {
  tab?: string;
  sectionId?: string;
  ruleId?: string;
  /** Matches WindowRouteParams' index signature so this can pass through openApp(). */
  [key: string]: unknown;
}

/**
 * Opens Settings as a singleton window (focus-if-open, matching every other
 * windowed app) when the current page has a real Space canvas to put it in.
 * Returns `false` when there's no Space context — StandaloneAppLayout,
 * marketing/landing pages via App.tsx's `?settings=` deep link — so the
 * caller can fall back to the legacy `UserSettingsModal` overlay, which is
 * the only place those pages can render Settings at all.
 */
export function openSettingsWindow(opts?: OpenSettingsOptions): boolean {
  if (useSpaceStore.getState().pageType !== 'space') return false;

  void windowRouter.openApp('settings', opts ?? {}).then(async (windowId) => {
    // openApp only threads `opts` through the route for a *newly created*
    // window — an already-open Settings window is just focused, with no
    // route update. Re-navigate it in place with the same params so
    // SettingsApp's `initialTab`/`initialSectionId` (read off the window
    // route) pick up the new tab/section too. This must NOT re-dispatch the
    // global `openSettings` event — SystemMenuButton's `openSettings`
    // listener calls back into `openSettingsWindow`, which would loop forever.
    if (opts?.tab || opts?.sectionId || opts?.ruleId) {
      await windowRouter.navigate(
        windowId,
        { appType: 'settings', params: opts },
        { replaceHistory: true },
      );
    }
  });

  return true;
}
